import type { BooleanMerkmal } from '../kapitel';
import type { RegelArt } from '../types';

/**
 * Der Vertrag des Textbaustein-Katalogs (Spezifikation E.3, erweitert um
 * `stufe`, `bedingungText` und `platzhalter` als abgeleitete Liste).
 *
 * Diese Datei ist absichtlich klein und ohne Logik: `katalog.ts` fuellt sie,
 * `automatik.ts` liest sie, und beide koennen getrennt gebaut und geprueft
 * werden, ohne dass eine Seite auf die andere wartet.
 *
 * ═══ RECHTSSCHRANKE (Spezifikation E.2) ═══════════════════════════════════
 * Jeder Baustein ist selbst formuliert. Vorschriften werden in
 * `vorschriften` nur BENANNT (Nummer und Titel), nie zitiert. Kein Wortlaut
 * aus DGUV-, BG-BAU- oder ASR-Veroeffentlichungen, kein Einlesen solcher
 * Texte als Quelle — weder hier noch im KI-Kontext.
 */

/**
 * Stufe der Vorlage (`docs/baustellenordnung/02-textbaustein-katalog.md`):
 *  - `pflicht`: ohne diesen Punkt ist die Ordnung unvollstaendig oder unwirksam;
 *  - `ueblich`: gehoert auf jede Baustelle dieser Art;
 *  - `optional`: nur bei entsprechender Projektlage.
 * Die Automatik schreibt `pflicht` und `ueblich` von selbst (wenn die
 * Bedingung erfuellt oder keine gesetzt ist), `optional` nie.
 */
export type BausteinStufe = 'pflicht' | 'ueblich' | 'optional';

export interface Baustein {
    /** Stabil, aus der Vorlage: `bo.notfall.notruf`. Eindeutig im Katalog. */
    id: string;
    /** Kapitelkennung aus `kapitel.ts` (z. B. `notfallmanagement`). */
    kapitel: string;
    /** Kurzbezeichnung fuer Listen („Notruf absetzen"). */
    titel: string;
    /** Der Satz, der ins Dokument geht — mit Platzhaltern in der Form `{name}`. */
    wortlaut: string;
    art: RegelArt;
    stufe: BausteinStufe;
    /** Nur BENANNTE Vorschriften, ohne Wortlaut. Leer ist gueltig. */
    vorschriften: string[];
    /**
     * Bedingung ueber Merkmale, ODER-verknuepft: der Baustein trifft zu,
     * sobald EINES der Merkmale ausdruecklich `true` ist. Fehlt die Liste
     * (und `bedingungText`), trifft er immer zu.
     */
    wenn?: BooleanMerkmal[];
    /** Ausschluss: entfaellt, sobald eines dieser Merkmale ausdruecklich `true` ist. */
    nichtWenn?: BooleanMerkmal[];
    /**
     * Bedingung, die KEIN Merkmal traegt („nur wenn ein Bauaufzug vorhanden
     * ist"). Ein solcher Baustein wird angeboten, nie von selbst geschrieben
     * — die Automatik kennt den Sachverhalt nicht.
     */
    bedingungText?: string;
    /** Namen der Platzhalter im Wortlaut, ohne Klammern, in Reihenfolge des Auftretens, ohne Doppelte. */
    platzhalter: string[];
    /** Die „Warnung" der Vorlage — fuer den Pruefer, nicht fuers Dokument. */
    hinweis?: string;
}

/** Alle `{name}`-Platzhalter eines Wortlauts, ohne Klammern, in Reihenfolge, ohne Doppelte. */
export const platzhalterImWortlaut = (wortlaut: string): string[] => {
    const treffer = (wortlaut || '').match(/\{[a-z0-9_]+\}/g) || [];
    return Array.from(new Set(treffer.map(t => t.slice(1, -1))));
};
